import { useEffect, useState } from "react";
import AddEmployeeFormModal from "../../components/AdminComponents/PagesComponents/Employees/AddEmployeeFormModal";
import EmployeesListComponent from "../../components/AdminComponents/PagesComponents/Employees/EmployeesListComponent";
import { addEmployee, getAllEmployees, getEmployeeMenuAccess, saveEmployeeMenuAccess, toggleEmployeeStatus, updateEmployee } from "../../services/AdminServices";
import MessageModal from "../../components/AdminComponents/Modals/MessageModal";
import ConfirmActionModal from "../../components/AdminComponents/Modals/ConfirmActionModal";
import EmployeeAccessModal from "../../components/AdminComponents/PagesComponents/Employees/EmployeeAccessModal";

const SAMPLE_MODULES = [
    { id: 1, name: "Proceedings" },
    { id: 2, name: "Approvals" },
    { id: 3, name: "Quick Review" },
    { id: 4, name: "Work" },
    { id: 5, name: "Work Not Started" },
    { id: 6, name: "Work Completed" },
    { id: 7, name: "Site Pictures" },
    { id: 8, name: "Accounts" },
    { id: 9, name: "Pending Payment" },
    { id: 10, name: "Alert" },
    { id: 11, name: "RTI" },
    { id: 12, name: "Statistics" },
    { id: 13, name: "News" },
    { id: 14, name: "Drone Video" },
    { id: 15, name: "Complaints" },
    { id: 16, name: "Voice" },
    { id: 17, name: "Attendance" },
];

const MODULE_KEY_MAP = {
    "Proceedings": "proceedings",
    "Approvals": "approvals",
    "Quick Review": "quickReview",
    "Work": "work",
    "Work Not Started": "workNotStarted",
    "Work Completed": "workCompleted",
    "Site Pictures": "sitePictures",
    "Accounts": "accounts",
    "Pending Payment": "pendingPayment",
    "Alert": "alert",
    "RTI": "rti",
    "Statistics": "statistics",
    "News": "news",
    "Drone Video": "droneVideo",
    "Complaints": "complaints",
    "Voice": "voice",
    "Attendance": "attendance",
};



const AdminEmployeesPage = () => {
    const [openModal, setOpenModal] = useState(false);
    const [accessModal, setAccessModal] = useState({
        open: false,
        employee: null,
    });
    const [editEmployee, setEditEmployee] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);


    const [employeeAccessMap, setEmployeeAccessMap] = useState({
        // employeeId: [moduleIds]
        1: [1, 2, 3],       // demo access
        2: [4, 7, 10],
    });

    const [messageModal, setMessageModal] = useState({
        open: false,
        type: "success",
        title: "",
        message: "",
    });

    const [confirmModal, setConfirmModal] = useState({
        open: false,
        designation: null,
    });

    const [accessConfirmModal, setAccessConfirmModal] = useState({
        open: false,
        employee: null,
        modules: [],
    });


    const showSuccess = (msg) => {
        setMessageModal({
            open: true,
            type: "success",
            title: "Success",
            message: msg,
        });
    };

    const showError = (msg) => {
        setMessageModal({
            open: true,
            type: "error",
            title: "Error",
            message: msg,
        });
    };

    const loadEmployees = async () => {
        try {
            setLoading(true);
            const res = await getAllEmployees();
            if (res.success) {
                setEmployees(res.data);
            }
        }catch(err){
            showError(err?.response?.data?.message || "Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadEmployees();
    }, []);

    const handleAdd = () => {
        setEditEmployee(null);
        setOpenModal(true);
    };

    const handleEdit = (emp) => {
        setEditEmployee(emp);
        setOpenModal(true);
    };

    const openEmployeeAccessModal = async (emp) => {
        try {
            const res = await getEmployeeMenuAccess(emp.id);

            if (res.success) {
                const activeModuleIds = SAMPLE_MODULES
                    .filter(m => res.data[MODULE_KEY_MAP[m.name]])
                    .map(m => m.id);

                setEmployeeAccessMap(prev => ({
                    ...prev,
                    [emp.id]: activeModuleIds
                }));
            }

            setAccessModal({
                open: true,
                employee: emp,
            });
        } catch (err) {
            showError(err?.response?.data?.message || "Failed to load employee access");
        }
    }
    const closeEmployeeAccessModal = () => {
        setAccessModal({ open: false, employee: null });
    }


    const handleSave = async (formData) => {
        try {
            let res;

            if (editEmployee) {
                res = await updateEmployee(editEmployee.id, formData);
            } else {
                res = await addEmployee(formData);
            }

            if (res.success) {
                await loadEmployees();
                setOpenModal(false);
                setEditEmployee(null);

                showSuccess(res.message || "Employee saved successfully");
            } else {
                showError(res.message || "Operation failed");
            }
        } catch (err) {
            showError(err?.response?.data?.message || "Something went wrong");
            throw err; // propagate to modal if needed
        }
    };

    const handleToggleRequest = (emp) => {
        setConfirmModal({ open: true, emp });
    };

    const handleConfirmToggle = async () => {
        try {
            await toggleEmployeeStatus(confirmModal.emp.id);
            await loadEmployees();
            showSuccess(confirmModal.emp.active ? "Employee deactivated successfully" : "Employee activated successfully")
        } catch (err) {
            showError(err?.response?.data?.message || "Something went wrong");
        } finally {
            setConfirmModal({ open: false, designation: null });
        }
    };

    const handleAccessSaveRequest = (employee, moduleIds) => {
        const payload = {
            employeeId: employee.id,
        };

        SAMPLE_MODULES.forEach((m) => {
            const key = MODULE_KEY_MAP[m.name];
            payload[key] = moduleIds.includes(m.id);
        });

        setAccessConfirmModal({
            open: true,
            employee,
            payload,
        });
    };

    const handleConfirmAccessSave = async () => {
        const { payload } = accessConfirmModal;

        try {
            const res = await saveEmployeeMenuAccess(payload);

            if (res.success) {
                showSuccess("Employee access updated successfully");
                closeEmployeeAccessModal();
            } else {
                showError(res.message || "Failed to update access");
            }
        } catch (err) {
            showError(err?.response?.data?.message || "Something went wrong");
        } finally {
            setAccessConfirmModal({
                open: false,
                employee: null,
                payload: null,
            });
        }
    };


    return (
        <div className="p-4 admin-card-box space-y-4">
            <div className="flex justify-between items-center">
                <h2 className="admin-heading text-blue">
                    Add / Edit Employee Details
                </h2>
                <button className="btnAdd" onClick={handleAdd}>
                    + Add New
                </button>
            </div>

            <EmployeesListComponent
                employees={employees}
                onEdit={handleEdit}
                onToggle={handleToggleRequest}
                onAccess={openEmployeeAccessModal}
                loading={loading}
            />

            <AddEmployeeFormModal
                open={openModal}
                onClose={() => setOpenModal(false)}
                onSave={handleSave}
                editData={editEmployee}
            />

            <EmployeeAccessModal
                open={accessModal.open}
                employee={accessModal.employee}
                modules={SAMPLE_MODULES}
                selectedModules={
                    accessModal.employee
                        ? employeeAccessMap[accessModal.employee.id] || []
                        : []
                }
                onSave={handleAccessSaveRequest}
                onClose={closeEmployeeAccessModal}
            />

            <ConfirmActionModal
                open={confirmModal.open}
                title={
                    confirmModal.emp?.active
                        ? "Deactivate Designation"
                        : "Activate Designation"
                }
                message={
                    confirmModal.emp?.active
                        ? "Do you really want to deactivate this employee?"
                        : "Do you want to activate this employee again?"
                }
                confirmText={
                    confirmModal.emp?.active ? "DEACTIVATE" : "ACTIVATE"
                }
                onConfirm={handleConfirmToggle}
                onClose={() =>
                    setConfirmModal({ open: false, emp: null })
                }
            />

            {/* ACCESS SAVE CONFIRM */}
            <ConfirmActionModal
                open={accessConfirmModal.open}
                title="Confirm Access Change"
                message="Changing access will affect employee permissions. Do you want to continue?"
                confirmText="YES, UPDATE ACCESS"
                onConfirm={handleConfirmAccessSave}
                onClose={() =>
                    setAccessConfirmModal({
                        open: false,
                        employee: null,
                        modules: [],
                    })
                }
            />

            <MessageModal
                open={messageModal.open}
                type={messageModal.type}
                title={messageModal.title}
                message={messageModal.message}
                onClose={() =>
                    setMessageModal({ ...messageModal, open: false })
                }
            />
        </div>
    )
}

export default AdminEmployeesPage;
